import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppPicker } from "@/modules/shell/AppPicker";
import { DetailReadout } from "@/modules/shell/DetailReadout";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  isDetailDraftDirty,
  NO_PERMISSION_EDIT,
} from "@/modules/shell/detail-edit-mode";
import { useCompanyHeld } from "@/modules/shell/use-company-held";
import { useDetailEditMode } from "@/modules/shell/use-detail-edit-mode";
import { listPriceLists } from "@/shared/api/inventory";
import { redeemOverrideOtp } from "@/shared/api/overrides";
import {
  deactivateParty,
  getParty,
  patchParty,
  reactivateParty,
} from "@/shared/api/parties";
import { renameCustomer } from "@/shared/api/sales";
import { renameSupplier } from "@/shared/api/purchasing";
import { notify } from "@/shared/feedback/notify";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

function roleLabel(p: {
  isCustomer: boolean;
  isSupplier: boolean;
}): string {
  if (p.isCustomer && p.isSupplier) return "Customer · Supplier";
  if (p.isCustomer) return "Customer";
  if (p.isSupplier) return "Supplier";
  return "—";
}

/** Contact detail — view first · Edit · dirty Update (plan/39 + plan/40). */
export function ContactDetailPage() {
  const { companySlug, partyId } = useParams();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const held = useCompanyHeld(companySlug);
  const [nameDraft, setNameDraft] = useState("");
  const [isCustomer, setIsCustomer] = useState(true);
  const [isSupplier, setIsSupplier] = useState(false);
  const [priceListId, setPriceListId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [pendingChangeId, setPendingChangeId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");

  const partyQuery = useQuery({
    queryKey: queryKeys.parties.detail(companySlug ?? "", partyId ?? ""),
    enabled: Boolean(companySlug && partyId),
    queryFn: () => getParty(companySlug!, partyId!),
  });
  const priceListsQuery = useQuery({
    queryKey: queryKeys.priceLists.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listPriceLists(companySlug!),
  });

  const errorMsg = queryErrorMessage(
    partyQuery.error ?? priceListsQuery.error,
    partyQuery.isError || priceListsQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const party = partyQuery.data?.party;

  const syncFromParty = useCallback(() => {
    if (!party) return;
    setNameDraft(party.name);
    setIsCustomer(party.isCustomer);
    setIsSupplier(party.isSupplier);
    setPriceListId(party.priceListId);
  }, [party]);

  useEffect(() => {
    syncFromParty();
  }, [syncFromParty]);

  const canSalesWrite = held.can("sales.write");
  const canPurchWrite = held.can("purchasing.write");
  const canEditRolesOrPrice =
    (party?.isCustomer && canSalesWrite) ||
    (party?.isSupplier && canPurchWrite) ||
    canSalesWrite ||
    canPurchWrite;
  /** Rename needs write for the party’s current customer/supplier side. */
  const canRename =
    (party?.isCustomer && canSalesWrite) ||
    (party?.isSupplier && canPurchWrite);
  const canEdit = Boolean(canRename || canEditRolesOrPrice);

  const deactivated = party?.deactivatedAt != null;
  const editBlockedReason = deactivated
    ? "Reactivate to edit"
    : !canEdit
      ? NO_PERMISSION_EDIT
      : null;

  const serverSnap = party
    ? {
        name: party.name,
        isCustomer: party.isCustomer,
        isSupplier: party.isSupplier,
        priceListId: party.priceListId,
      }
    : null;
  const draftSnap = {
    name: nameDraft.trim(),
    isCustomer,
    isSupplier,
    priceListId,
  };
  const dirty = serverSnap
    ? isDetailDraftDirty(draftSnap, serverSnap)
    : false;

  const edit = useDetailEditMode({
    dirty,
    canEdit: canEdit && !deactivated,
    editBlockedReason,
    objectLabel: party?.name ?? "Contact",
    confirm,
    onResetDrafts: syncFromParty,
  });

  async function reload() {
    if (!companySlug || !partyId) return;
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.parties.all(companySlug),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.parties.detail(companySlug, partyId),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.customers.all(companySlug),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.suppliers.all(companySlug),
      }),
    ]);
  }

  if (!companySlug || !partyId) return null;

  if (errorMsg && !party) {
    return (
      <Alert variant="destructive" className="max-w-3xl">
        {errorMsg}
      </Alert>
    );
  }
  if (partyQuery.isPending || !party) {
    return (
      <Alert variant="muted" className="max-w-3xl">
        Loading contact…
      </Alert>
    );
  }

  const p = party;
  const priceLists = priceListsQuery.data?.priceLists ?? [];
  const listLabel = (id: string | null) => {
    if (!id) return "Org default";
    return priceLists.find((l) => l.id === id)?.name ?? "List";
  };

  async function onUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty || !nameDraft.trim()) return;
    if (!isCustomer && !isSupplier) {
      setError("Select at least one role");
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const nameChanged = nameDraft.trim() !== p.name;
      const rolesOrPriceChanged =
        isCustomer !== p.isCustomer ||
        isSupplier !== p.isSupplier ||
        priceListId !== p.priceListId;

      if (rolesOrPriceChanged) {
        await patchParty(companySlug!, partyId!, {
          isCustomer,
          isSupplier,
          priceListId: isCustomer ? priceListId : null,
          expectedUpdatedAt: p.updatedAt,
          base: {
            isCustomer: p.isCustomer,
            isSupplier: p.isSupplier,
            priceListId: p.priceListId,
          },
        });
      }

      if (nameChanged) {
        const rename = isCustomer
          ? renameCustomer(companySlug!, partyId!, nameDraft)
          : renameSupplier(companySlug!, partyId!, nameDraft);
        const res = await rename;
        if (res.applied) {
          setPendingChangeId(null);
          const name =
            "customer" in res ? res.customer.name : res.supplier.name;
          setStatus(`Updated ${name}`);
          notify.updated(name);
        } else {
          setPendingChangeId(res.changeRequest.id);
          setStatus("Rename queued — Approvals or emergency OTP below");
          notify.requestSent("rename");
        }
      } else if (rolesOrPriceChanged) {
        notify.updated(p.name);
        setStatus(`Updated ${p.name}`);
      }

      await reload();
      edit.markSaved();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed";
      setError(msg);
      notify.error(msg);
      await reload();
      syncFromParty();
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageFrame>
      <DeskDocBackLink
        listBasePath={`/c/${companySlug}/contacts`}
        listLabel="Contacts"
      />
      <PageHeader
        className="mt-3"
        title={p.name}
        description={roleLabel(p)}
        eyebrow={
          edit.isEditing ? (
            <span>
              Contact ·{" "}
              <span className="text-[var(--color-foreground)]">Editing</span>
            </span>
          ) : deactivated ? (
            <Badge variant="outline">Deactivated</Badge>
          ) : null
        }
        actions={
          edit.isEditing ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void edit.leaveEdit()}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="contact-master-form"
                size="sm"
                pending={busy}
                pendingLabel="Updating…"
                disabled={
                  !dirty ||
                  !nameDraft.trim() ||
                  (!isCustomer && !isSupplier)
                }
              >
                Update
              </Button>
            </>
          ) : (
            <span title={edit.editTitle}>
              <Button
                type="button"
                size="sm"
                disabled={!canEdit || deactivated || busy}
                onClick={() => edit.enterEdit()}
              >
                Edit
              </Button>
            </span>
          )
        }
      />
      {edit.showEditBlockedHint ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          {edit.editBlockedReason}
        </p>
      ) : null}

      {error ? (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      ) : null}
      {status ? (
        <Alert variant="muted" className="mt-4">
          {status}
        </Alert>
      ) : null}

      <section className="mt-6 max-w-lg space-y-3">
        <h2 className="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
          Identity
        </h2>
        {edit.isEditing ? (
          <form
            id="contact-master-form"
            className="space-y-4"
            onSubmit={(e) => void onUpdate(e)}
          >
            {p.code ? (
              <p className="m-0 text-sm text-[var(--color-muted)]">
                Code{" "}
                <span className="font-mono text-[var(--color-foreground)]">
                  {p.code}
                </span>
              </p>
            ) : null}
            <label className="block text-sm">
              <span className="text-[var(--color-muted)]">Name</span>
              <Input
                ref={edit.firstFieldRef}
                className="mt-1"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                required
                disabled={busy}
                aria-label="Contact name"
              />
            </label>
            <p className="m-0 text-xs text-[var(--color-muted)]">
              Renaming is a gated identity change — may queue for approval.
            </p>

            <div className="space-y-2">
              <h3 className="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
                Roles
              </h3>
              <div className="flex flex-col gap-2 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isCustomer}
                    disabled={busy || (isCustomer && !isSupplier)}
                    onChange={(e) => setIsCustomer(e.target.checked)}
                  />
                  Customer
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isSupplier}
                    disabled={busy || (isSupplier && !isCustomer)}
                    onChange={(e) => setIsSupplier(e.target.checked)}
                  />
                  Supplier
                </label>
              </div>
            </div>

            {isCustomer ? (
              <div className="space-y-2">
                <h3 className="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
                  Default price list
                </h3>
                <AppPicker
                  title="Price list"
                  description={`Default list for ${nameDraft.trim() || p.name}`}
                  placeholder="Org default"
                  clearLabel="Org default"
                  value={priceListId}
                  disabled={busy}
                  options={priceLists.map((l) => ({
                    value: l.id,
                    label: l.name,
                    keywords: l.name,
                  }))}
                  emptyLabel="No price lists"
                  onChange={(id) => setPriceListId(id)}
                />
              </div>
            ) : null}
          </form>
        ) : (
          <DetailReadout
            rows={[
              ...(p.code
                ? [{ label: "Code", value: p.code, mono: true as const }]
                : []),
              { label: "Name", value: p.name },
              { label: "Roles", value: roleLabel(p) },
              ...(p.isCustomer
                ? [
                    {
                      label: "Default price list",
                      value: listLabel(p.priceListId),
                    },
                  ]
                : []),
            ]}
          />
        )}
      </section>

      {pendingChangeId ? (
        <div className="mt-4 max-w-lg">
          <Label htmlFor="party-otp">Emergency OTP from manager</Label>
          <div className="mt-1 flex gap-2">
            <Input
              id="party-otp"
              className="min-w-0 flex-1 font-mono tracking-widest"
              inputMode="numeric"
              placeholder="••••••"
              maxLength={8}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              disabled={busy}
            />
            <Button
              type="button"
              variant="outline"
              disabled={busy || otpCode.trim().length < 4}
              onClick={() => {
                setBusy(true);
                setError(null);
                void redeemOverrideOtp(companySlug, {
                  code: otpCode.trim(),
                  requestId: pendingChangeId,
                  requestKind: "change",
                })
                  .then(() => {
                    setPendingChangeId(null);
                    setOtpCode("");
                    setStatus("OTP accepted — rename applied");
                    return reload();
                  })
                  .catch((err: unknown) => {
                    const msg =
                      err instanceof Error ? err.message : "OTP failed";
                    setError(msg);
                    notify.error(msg);
                  })
                  .finally(() => setBusy(false));
              }}
            >
              Use
            </Button>
          </div>
        </div>
      ) : null}

      <section className="mt-8 max-w-lg space-y-2">
        <h2 className="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
          Due
        </h2>
        <div className="space-y-1 text-sm font-mono tabular-nums">
          {p.isCustomer && Number(p.outstandingAr ?? 0) > 0 ? (
            <div>
              {moneyDisplay(p.outstandingAr!)} ·{" "}
              <Link
                to={`/c/${companySlug}/sales/to-collect`}
                className="font-sans no-underline"
              >
                AR
              </Link>
            </div>
          ) : null}
          {p.isSupplier && Number(p.outstandingAp ?? 0) > 0 ? (
            <div>
              {moneyDisplay(p.outstandingAp!)} ·{" "}
              <Link
                to={`/c/${companySlug}/purchasing/to-pay`}
                className="font-sans no-underline"
              >
                AP
              </Link>
            </div>
          ) : null}
          {!(Number(p.outstandingAr ?? 0) > 0) &&
          !(Number(p.outstandingAp ?? 0) > 0) ? (
            <span className="font-sans text-[var(--color-muted)]">—</span>
          ) : null}
        </div>
      </section>

      <section className="mt-10 max-w-lg border-t border-[var(--color-border)] pt-6">
        <h2 className="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
          Status
        </h2>
        {!deactivated ? (
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            disabled={busy || edit.isEditing}
            onClick={() => {
              void (async () => {
                const ok = await confirm({
                  title: `Deactivate ${p.name}?`,
                  description:
                    "Hidden from new sales and purchase documents. You can reactivate later.",
                  confirmLabel: "Deactivate",
                  destructive: true,
                });
                if (!ok) return;
                setBusy(true);
                setError(null);
                try {
                  await deactivateParty(companySlug, partyId);
                  notify.updated(`${p.name} · deactivated`);
                  await reload();
                } catch (err: unknown) {
                  const msg = err instanceof Error ? err.message : "Failed";
                  setError(msg);
                  notify.error(msg);
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            Deactivate
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              void reactivateParty(companySlug, partyId)
                .then(() => {
                  notify.updated(`${p.name} · reactivated`);
                  return reload();
                })
                .catch((err: unknown) => {
                  const msg = err instanceof Error ? err.message : "Failed";
                  setError(msg);
                  notify.error(msg);
                })
                .finally(() => setBusy(false));
            }}
          >
            Reactivate
          </Button>
        )}
      </section>

      <p className="mt-10 text-sm">
        <Link to={`/c/${companySlug}/sales/orders`} className="no-underline">
          Sales orders
        </Link>
        {" · "}
        <Link
          to={`/c/${companySlug}/purchasing/orders`}
          className="no-underline"
        >
          Purchase orders
        </Link>
        {" · "}
        <Link to={`/c/${companySlug}/approvals`} className="no-underline">
          Approvals
        </Link>
      </p>
    </PageFrame>
  );
}
